import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { ThreeElements, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { misc } from 'maath';
import { MotionPathContext, MotionPathState } from './MotionPathContext';

/// Helper variables
const v3 = /* @__PURE__ */ new THREE.Vector3();

export type MotionPathRef = THREE.Group & { motion: MotionPathState };

export type MotionPathControlsProps = ThreeElements['group'] & {
  /**
   * The object to control, if not provided, it will use the camera
   */
  object?: React.RefObject<THREE.Object3D>;
  /**
   * Whether to enable motion path controls
   */
  motion?: boolean;
  /**
   * The curve to follow, it can be a THREE.Curve or a THREE.CurvePath
   */
  curve: THREE.Curve<THREE.Vector3> | THREE.CurvePath<THREE.Vector3>;
  /**
   * Whether to make this the default controls, if true, it will replace the current controls
   */
  makeDefault?: boolean;
  /**
   * The tracker object, this is the object that will be used to look at
   */
  tracker: React.RefObject<THREE.Object3D>;
  /**
   * The offset to start from, value between 0 and 1
   * @default 0
   */
  offset?: number;
  /**
   * Whether to loop the path, if true, it will repeat the path
   * @default true
   */
  loop?: boolean;
  /**
   * Focal offset, this is the offset to apply to the tracker object
   */
  focalOffset?: THREE.Vector3 | [number, number, number];
};

export const MotionPathControls = forwardRef<MotionPathRef, MotionPathControlsProps>(
  (
    {
      object,
      curve,
      motion = true,
      makeDefault = false,
      loop = true,
      offset = 0,
      tracker,
      children,
      focalOffset,
      ...restProps
    },
    fowardedRef,
  ) => {
    const internalRef = useRef<MotionPathRef>(null!);
    const camera = useThree(({ camera }) => camera);
    const controls = useThree(({ controls }) => controls);
    const get = useThree((state) => state.get);
    const set = useThree((state) => state.set);
    const pos = useRef<number>(offset);
    const offsetVec = useMemo(
      () =>
        Array.isArray(focalOffset)
          ? new THREE.Vector3(...focalOffset)
          : focalOffset?.clone?.() || new THREE.Vector3(),
      [focalOffset],
    );

    const path = useMemo(() => {
      const path = new THREE.CurvePath<THREE.Vector3>();
      path.curves = [new THREE.CatmullRomCurve3(curve.getPoints(100))];
      path.updateArcLengths();
      return path;
    }, [curve]);

    const state = useMemo<MotionPathState>(
      () => ({
        tracker,
        object: object ? object : { current: camera },
        path,
        current: pos.current,
        offset: pos.current,
        point: new THREE.Vector3(),
        tangent: new THREE.Vector3(),
        next: new THREE.Vector3(),
      }),
      [camera, path, object, tracker],
    );

    useImperativeHandle(fowardedRef, () => Object.assign(internalRef.current, { motion: state }), [
      state,
    ]);

    useFrame(({ invalidate }) => {
      if (!motion) return;
      const lastOffset = state.offset;
      state.offset = pos.current = loop
        ? misc.repeat(state.current, 1)
        : misc.clamp(state.current, 0, 1);
      path.getPoint(state.offset, v3);
      state.object.current.position.lerp(v3, 0.1);
      state.object.current.lookAt(state.tracker.current.position);
      path.getPointAt(state.offset, state.point);
      path.getTangentAt(state.offset, state.tangent).normalize();
      path.getPointAt(misc.repeat(pos.current - (lastOffset - state.offset), 1), state.next);
      if (offsetVec.length() > 0) {
        const worldOffset = offsetVec.clone().applyQuaternion(state.object.current.quaternion);
        state.object.current.lookAt(state.tracker.current.position.clone().sub(worldOffset));
      }
      invalidate(1);
    });

    useEffect(() => {
      if (makeDefault) {
        const old = get().controls;
        set({ controls });
        return () => set({ controls: old });
      }
    }, [get, set, makeDefault, controls]);

    return (
      <group ref={internalRef} {...restProps}>
        <MotionPathContext value={state}>{children}</MotionPathContext>
      </group>
    );
  },
);
